/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import React from 'react';
import {Checkbox} from "./checkbox";
import {ControlContext} from "../util/controlContext";
import {generateComponentId} from '../../..';

interface Props {
    onCheck: Function;
    onMouseEnter?: Function;
    onMouseLeave?: Function;
    checked: boolean;
    disabled: boolean;
    label: string;
    labelPosition: 'left' | 'right';
    containerClass: string;
    labelClass: string;
    checkboxClass: string;
    checkClass: string;
    errorClass: string;
    baseColor?: string;
    contrastColor?: string;
    error: string | null;
    id?: string;
    checkIcon: React.ReactNode;
    unControlled: boolean;
    title?: string;
    useDarkestColor: boolean;
}

interface CheckboxState {
    isHovered: boolean;
    internalValue: boolean;
}

export default class CheckboxController extends React.Component<Props, CheckboxState> {
    static contextType = ControlContext;

    static defaultProps = {
        disabled: false,
        label: '',
        labelPosition: 'right',
        containerClass: '',
        labelClass: '',
        checkboxClass: '',
        checkClass: '',
        errorClass: '',
        checkIcon: null,
        error: null,
        unControlled: false,
        useDarkestColor: true,
    };

    private getContainer = (): HTMLElement | null => {
        return document.getElementById(this.id);
    };

    private getRipple = (): HTMLElement | null => {
        const container = this.getContainer();
        if (container === null) return null;
        return container.querySelector('div.ripple');
    };

    private id: string = generateComponentId(this.props.id);

    state = {isHovered: false, internalValue: this.props.checked};

    onCheck = (evt: React.MouseEvent<Element, Event>) => {
        const {checked, onCheck, disabled, unControlled} = this.props;
        if (disabled) return;
        if (unControlled) {
            evt.stopPropagation();
            evt.persist();
            this.setState({internalValue: !this.state.internalValue}, () => {
                onCheck(this.state.internalValue, evt);
            });
        } else onCheck(!checked, evt);
        this.addRipple();
    };

    onMouseEnter = () => {
        if (this.props.disabled) return;
        this.setState({isHovered: true});
    };

    onMouseLeave = () => {
        if (this.props.disabled) return;
        this.setState({isHovered: false});
    };

    addRipple = () => {
        const container = this.getContainer();
        if (container !== null) {
            const ripple = this.getRipple();
            if (ripple !== null) {
                if (container.classList.contains('rippling'))
                    container.classList.remove('rippling');
                ripple.addEventListener('transitionend', this.removeRipple);
                container.classList.add('rippling');
            }
        }
    };

    removeRipple = () => {
        const ripple = this.getRipple();
        const container = this.getContainer();
        if (ripple !== null && container !== null) {
            ripple.removeEventListener('transitionend', this.removeRipple);
            container.classList.remove('rippling');
        }
    };

    componentWillUnmount = () => {
        this.removeRipple();
    };

    render = () => {
        const {
            checked, disabled, label, labelPosition, containerClass, labelClass, checkboxClass, checkClass, checkIcon,
            error, errorClass, unControlled, title, baseColor, contrastColor, useDarkestColor,
        } = this.props, {isHovered, internalValue} = this.state, isChecked = unControlled ? internalValue : checked;
        return <Checkbox onCheck={this.onCheck} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}
                         checked={isChecked} disabled={disabled} isHovered={isHovered} label={label}
                         labelPosition={labelPosition} containerClass={containerClass} labelClass={labelClass}
                         checkboxClass={checkboxClass} checkClass={checkClass} id={this.id} checkIcon={checkIcon}
                         error={error} errorClass={errorClass} title={title} baseColor={baseColor}
                         contrastColor={contrastColor} useDarkestColor={useDarkestColor}/>
    }
}